import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { Menu } from 'lucide-react';

const useStyles = makeStyles({
    // Glyph-wide, so the layout around it owns the spacing.
    button: {
        minWidth: 'auto',
        paddingLeft: 0,
        paddingRight: 0,
        color: tokens.colorCompoundBrandForeground1,
        ':disabled': { color: tokens.colorNeutralForegroundDisabled },
        '& .fui-Button__icon, & svg': {
            width: tokens.fontSizeBase600,
            height: tokens.fontSizeBase600,
            fontSize: tokens.fontSizeBase600,
        },
    },
});

type HeaderIconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'aria-label'> & {
    icon: React.ReactElement;
    /** Translated. The icon carries no accessible name, so the button is announced by this. */
    label: string;
};

/** An icon-only control of the app chrome (header, sidebar head). Forwards its ref, so it can trigger a menu. */
export const HeaderIconButton = React.forwardRef<HTMLButtonElement, HeaderIconButtonProps>(
    ({ className, label, ...props }, ref) => {
        const styles = useStyles();
        return (
            <Button
                ref={ref}
                appearance="transparent"
                aria-label={label}
                className={mergeClasses(styles.button, className)}
                {...props}
            />
        );
    },
);
HeaderIconButton.displayName = 'HeaderIconButton';

/** The menu toggle that opens the sidebar or the navigation panel. */
export const HamburgerButton = (props: Omit<HeaderIconButtonProps, 'icon' | 'label'>) => {
    const { t } = useTranslation();
    return <HeaderIconButton icon={<Menu />} label={t('Menu')} {...props} />;
};
